import logger from '@/modules/Logger';
import store from '@/store/index';
import { AUDIO_ONLY_FALLBACK, NETWORK_QUALITY } from '@/modules/webrtc/agora/const';

/**
 * Agora UID scheme (see backend AgoraTokenProvider::computeRetryUid):
 *   {prefix}{rawUserId}{2-digit suffix}
 *   - prefix: '1' = student, '2' = trainer
 *   - suffix: '01', '02', '03'... incremented on each UID-conflict retry
 *
 * Example (student, rawUserId 261120): 126112001, 126112002, 126112003
 *
 * Stripping the trailing 2-digit suffix yields a stable per-user "base" that is
 * shared across all retry variants of the same participant.
 */
const STUDENT_PREFIX = '1';
const TRAINER_PREFIX = '2';

// Stable base for a given Agora uid (prefix + rawUserId, suffix removed)
function getBaseUid(uid) {
  return String(uid).slice(0, -2);
}

// True when the uid belongs to the local user (original or any retry variant)
function isOwnUid(uid) {
  const userId = store.state.userId;
  if (!userId) {
    return false;
  }
  const ownPrefix = store.state.accType === 's' ? STUDENT_PREFIX : TRAINER_PREFIX;
  return getBaseUid(uid) === ownPrefix + String(userId);
}

// True when two uids are retry variants of the same remote participant
function isSameRemoteUser(uidA, uidB) {
  return getBaseUid(uidA) === getBaseUid(uidB);
}

export default {
  // set on event to agora connection
  setOnEvent() {
    // on remote user media published
    this.on('user-published', async (remoteUser, mediaType) => {
      try {
        const uid = remoteUser.uid;

        // Ignore our own uid (original or retry variant) showing up as remote
        if (isOwnUid(uid)) {
          logger.info('[Agora] Ignoring own UID in user-published: ' + uid, { operation: 'webrtc:remote-user-published' });
          return;
        }

        // If another retry variant of the same remote user is already tracked,
        // compare UIDs to determine which is the newer retry. A higher UID means a
        // later retry attempt — only replace if the new UID is higher than the existing one.
        // If the new UID is lower, it's the stale ghost and should be ignored.
        const existingEntry = this.remoteUsers.find(
          item => item.peerId !== uid && isSameRemoteUser(item.peerId, uid)
        );
        if (existingEntry && Number(uid) <= Number(existingEntry.peerId)) {
          logger.info('[Agora] Ignoring stale UID ' + uid + ' (existing UID ' + existingEntry.peerId + ' is higher or equal)', { operation: 'webrtc:uid-priority' });
          return;
        }
        if (existingEntry) {
          logger.info('[Agora] Replacing stale UID ' + existingEntry.peerId + ' with higher UID ' + uid, { operation: 'webrtc:uid-priority' });

          try {
            const staleRemote = this.rtc.remoteUsers.find(u => u.uid === existingEntry.peerId);
            if (staleRemote) {
              await this.rtc.unsubscribe(staleRemote);
            }
          } catch (e) {
            logger.info('[Agora] Unsubscribe from stale UID failed: ' + e.message, { operation: 'webrtc:uid-priority' });
          }

          for (let idx = this.remoteUsers.length - 1; idx >= 0; idx--) {
            if (this.remoteUsers[idx].peerId === existingEntry.peerId) {
              this.remoteUsers.splice(idx, 1);
            }
          }

          // Drop stale tracks so the new MediaStream is built only from the live uid
          this.remoteUserTrack = [];
        }

        // Subscribe to a remote user.
        await this.rtc.subscribe(remoteUser, mediaType);
        this.remoteUserRtc = remoteUser;

        // filter same remote user data
        this.remoteUserExists(remoteUser.uid);
        this.remoteUserTrack = this.remoteUserTrack.filter(item => item.trackMediaType !== mediaType)

        // Get remote user media tracks
        if (mediaType === "video" && remoteUser.hasVideo) {
          this.remoteUserTrack.push(remoteUser.videoTrack);
          logger.info('[Agora] Subscribed to remote ' + mediaType, { operation: 'webrtc:remote-video' });
        }

        if (mediaType === "audio" && remoteUser.hasAudio) {
          this.playRemoteAudioTrack();
          this.remoteUserTrack.push(remoteUser.audioTrack);
          logger.info('[Agora] Subscribed to remote ' + mediaType, { operation: 'webrtc:remote-audio' });

          // A fresh audio track (initial subscribe, or re-subscribe when the peer
          // shares screen / reconnects) plays through Agora's internal element on the
          // OS default output. Re-apply the selected speaker or remote audio silently
          // falls back to the PC speaker.
          if (this.audioOutputDevice) {
            await this.setAudioOutputDevice(this.audioOutputDevice);
          }

          this.checkIceCandidates();
          this.rtc.setStreamFallbackOption(remoteUser.uid, AUDIO_ONLY_FALLBACK);
        }

        const remoteStream = new MediaStream();
        this.remoteUserTrack.forEach(track => {
          const mediaTrack = track.getMediaStreamTrack();
          if (mediaTrack) {
            remoteStream.addTrack(mediaTrack);
          }
        });
        this.remoteUsers.push({ peerId: remoteUser.uid, stream: remoteStream });

        // If the video track is ended (stale after reconnection), schedule a refresh
        // to re-build the stream once Agora delivers a live track
        if (mediaType === 'video') {
          const videoTrack = remoteUser.videoTrack ? remoteUser.videoTrack.getMediaStreamTrack() : null;
          if (!videoTrack || videoTrack.readyState !== 'live') {
            logger.info('[Agora] Video track not live after subscribe, scheduling refresh', {
              operation: 'webrtc:remote-video',
              readyState: videoTrack ? videoTrack.readyState : 'null',
              lesson_id: store.state.lessonId
            });
            this.refreshRemoteStreamWithRetry(0);
          }
        }

        // Force autoplay for remote video to bypass old iOS permission restrictions
        if (!this.localTracks.length && mediaType === 'video') {
          setTimeout(() => {
            const remoteVideo = document.getElementById('remoteVideo');
            if (remoteVideo) {
              remoteVideo.muted = true;
              // Once playback starts, re-enable the audio
              remoteVideo.play().then(() => {
                setTimeout(() => remoteVideo.muted = false, 2000);
              });
            }
          }, 500);
        }
      } catch (err) {
        logger.error('[Agora] Subscribe stream failed', {
          operation: 'webrtc:remote-user-published',
          err: err.message,
          event_type: 44,
          lesson_id: store.state.lessonId
        });
      }
    });

    // on remote user join
    this.on('user-joined', (user) => {
      // Ignore our own uid (original or retry variant) showing up as remote
      if (isOwnUid(user.uid)) {
        logger.info('[Agora] Ignoring own UID in user-joined: ' + user.uid, { operation: 'webrtc:remote-user-join' });
        return;
      }

      logger.info('[Agora] Remote user joined: ' + user.uid, { operation: 'webrtc:remote-user-join' });
      // Delay check to give time on remote user device publishing
      setTimeout(() => {
        if (!user.hasVideo && !user.hasAudio) {
          // If a retry variant of the same remote user already has an active stream,
          // compare UIDs to determine which is newer. Only ignore this join if the
          // existing active variant has a higher UID (meaning it's a later retry).
          const activeVariant = this.remoteUsers.find(
            item => isSameRemoteUser(item.peerId, user.uid) && item.stream
          );
          if (activeVariant && Number(user.uid) <= Number(activeVariant.peerId)) {
            logger.info('[Agora] Ignoring stale ghost UID ' + user.uid + ' (active variant ' + activeVariant.peerId + ' has higher or equal UID)', { operation: 'webrtc:remote-user-join' });
            return;
          }

          // Remove any stale media-less variant entries before adding this one
          for (let idx = this.remoteUsers.length - 1; idx >= 0; idx--) {
            if (isSameRemoteUser(this.remoteUsers[idx].peerId, user.uid) && !this.remoteUsers[idx].stream) {
              this.remoteUsers.splice(idx, 1);
            }
          }

          logger.info('[Agora] Remote user has no microphone and camera', { operation: 'webrtc:remote-user-join' });
          this.remoteUsers.push({ peerId: user.uid });
        }
      }, 1000);
    })

    // on remote leave the channel
    this.on('user-left', (user, reason) => {
      logger.info('[Agora] Remote user left: ' + user.uid, { operation: 'webrtc:remote-user-left', reason: reason });

      for (let idx in this.remoteUsers) {
        if (this.remoteUsers[idx].peerId === user.uid) {
          // Set the remote user stream as null instead of removing when only switching stream
          if (reason === 'BecomeAudience') {
            this.remoteUsers[idx].stream = null;
          } else {
            this.remoteUsers.splice(idx, 1);
          }
        }
      }

      // Only clear remote tracks when the CURRENTLY active remote user leaves.
      // A late "user-left" for a stale (already-replaced) UID must not wipe the
      // tracks of the new, live remote session.
      if (reason !== 'BecomeAudience' && this.remoteUserRtc && this.remoteUserRtc.uid === user.uid) {
        this.remoteUserTrack = [];
      }
    });

    // on local stream connection state change
    this.on('connection-state-change', (curState, revState, reason) => {
      // emit rtc connection current state
      if (curState === 'CONNECTED') {
        store.commit('connection/setHasConnectedOnce');
        store.commit('connection/setReconnecting', false);
        this.socket.emit('signal', { type: 'rtc', connected: true });

        // After reconnection from RECONNECTING state, restore full video.
        // During disconnect, audioOnly may have been activated (unsubscribed from video).
        // Re-subscribe to video and reset the audioOnly flag.
        if (revState === 'RECONNECTING') {
          store.commit('videoRtc/setAudioOnly', false);

          if (this.remoteUserRtc) {
            this.refreshRemoteStreamWithRetry(0);
          }
        }
      } else if (curState === 'RECONNECTING') {
        store.commit('connection/setReconnecting', true);
      } else if (curState === 'DISCONNECTED') {
        if (reason !== 'LEAVE') {
          // Only clear reconnecting if browser is online.
          // If offline, keep the overlay visible until network returns.
          if (navigator.onLine) {
            store.commit('connection/setReconnecting', false);
          }
        }
        this.socket.emit('signal', { type: 'rtc', connected: false });
        if (reason !== 'LEAVE') {
          logger.error('[Agora] Disconnected on server', { operation: 'webrtc:connection', reason: reason,event_type:45,lesson_id:this.lessonId });
        }
        if (reason === 'UID_BANNED') {
          store.commit('videoRtc/setPeerError', { code: 'UID_BANNED' });
        }
      }
    });

    // on quality issues
    this.on('exception', (message) => {
      if (this.enableAudio && message.msg.includes('AUDIO') && store.getters.isLessonOngoing) {
        switch (message.msg) {
          case 'AUDIO_INPUT_LEVEL_TOO_LOW':
            logger.warn('[Agora] User microphone input level is too low - remote users may not hear clearly', { 
              operation: 'audio:stream-quality',
              reason: message,
              event_type: 46,
              lesson_id: store.state.lessonId,
            });
            break;

          case 'AUDIO_INPUT_LEVEL_TOO_LOW_RECOVER':
            logger.warn('[Agora] User microphone input level was too low but recovered - audio clarity restored for remote users',{ 
                operation: 'audio:stream-quality',
                reason: message,
                event_type: 46,
                lesson_id: store.state.lessonId,
            });
            break;
            
          case 'SEND_AUDIO_BITRATE_TOO_LOW':
            logger.warn('[Agora] User audio quality is poor due to low network bandwidth - remote users may not hear clearly',{ 
                operation: 'audio:stream-quality',
                reason: message,
                event_type: 46,
                lesson_id: store.state.lessonId,
            });
            break;

          case 'SEND_AUDIO_BITRATE_TOO_LOW_RECOVER':
            logger.warn('[Agora] User audio quality was poor but has now improved - audio clarity restored for remote users',{ 
                operation: 'audio:stream-quality',
                reason: message,
                event_type: 46,
                lesson_id: store.state.lessonId,
            });
            break;

          default:
            logger.error('[Agora] Connection quality exception', { 
                operation: 'audio:stream-quality',
                reason: message,
                event_type: 46,
                lesson_id: store.state.lessonId,
            });
            break;      
        }
      }
    });

    this.on('network-quality', async (quality) => {
      if (quality.downlinkNetworkQuality === NETWORK_QUALITY.unknown) {
        return;
      }

      this.setQualityOnDownLinkSpeed(quality.downlinkNetworkQuality);
      this.setQualityOnUpLinkSpeed(quality.uplinkNetworkQuality);
    });
  }
}
